import * as React from "react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils"; // Assuming you have a utility function like this
import { buttonVariants } from "@/components/ui/button"; // Assuming you have button variants defined

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Calendar component updated for react-day-picker v9.
 *
 * @param className - Additional CSS classes for the root element.
 * @param classNames - Object to customize CSS classes for internal elements.
 * @param showOutsideDays - Whether to display days belonging to previous/next months.
 * @param props - Other props passed down to the DayPicker component.
 */
function AvaliableCalendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      captionLayout="label"
      showOutsideDays={showOutsideDays}
      timeZone="Asia/Bangkok"
      // The timeZone prop is deprecated in v9. Time zone handling should be managed externally.
      // See: https://daypicker.dev/guides/localization#timezone
      className={cn("p-3")}
      classNames={{
        // root: "w-full",
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-csenter absolute right-0",
        nav_button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
        ),
        nav_button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
        ),
        // table: "w-full border-collapse space-y-1",
        // head_row: "w-full",
        // head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        // row: "grid grid-cols-7",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
          // "h-9 w-9",
          // วันที่อยู่ในช่วงที่เลือก
          "[&:has(.day-range-end)]:rounded-r-md",
          "[&:has(.day-range-start)]:rounded-l-md",
          "[&:has(.day-range-middle)]:bg-accent"
        ),
        day: cn(
          // buttonVariants({ variant: "ghost" }),
          "rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
          "hover:bg-accent hover:text-accent-foreground",
          "p-0 font-normal aria-selected:opacity-100 text-center",
        ),
        day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
        day_today: "bg-blue-400 text-white",
        day_outside: "text-muted-foreground opacity-50",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_start: "day-range-start rounded-l-md",
        day_range_end: "day-range-end rounded-r-md",
        day_range_middle: "day-range-middle bg-accent text-accent-foreground",
        day_hidden: "invisible",
        month_grid: "w-full md:w-[250px]",
        weekday: "text-center",
        week: "gap-2",
      }}
      modifiersClassNames={{
        selected: "bg-primary text-primary-foreground",
        today: "bg-blue-400 text-white",
        outside: "text-muted-foreground opacity-50",
        disabled: "text-muted-foreground opacity-50",
        range_start: "day-range-start",
        range_end: "day-range-end",
        range_middle: "day-range-middle",
      }}
        
      {...props} // Pass remaining props to DayPicker
    />
  );
}
AvaliableCalendar.displayName = "AvaliableCalendar";

export { AvaliableCalendar };
